import type { FontVariant } from '@onefootprint/design-tokens';
import { cx } from 'class-variance-authority';
import type React from 'react';
import type { InputHTMLAttributes } from 'react';
import { forwardRef, useEffect, useId } from 'react';
import mergeRefs from 'react-merge-refs';
import { useFormattedValues } from '../../../hooks/use-formatted-values';
import { useInputMask } from '../../../hooks/use-input-mask';
import type { MaskConfig } from '../../../utils/mask-utils';
import Hint from '../../hint';
import Label from '../../label';
import type { FieldProps } from '../field';

export type InternalInputProps = {
  prefixComponent?: React.ReactNode;
  suffixComponent?: React.ReactNode;
  fontVariant?: FontVariant;
};

type NativeInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>;

export type InputProps = {
  value?: string;
  mask?: MaskConfig;
  containerClassName?: string;
} & NativeInputProps &
  FieldProps;

type AllInputProps = InputProps & InternalInputProps;

const BaseInput = forwardRef<HTMLInputElement, AllInputProps>(
  (
    {
      className,
      containerClassName,
      disabled = false,
      hasError = false,
      hasFocus = false,
      hint,
      id: baseID,
      label,
      mask,
      onChange,
      onChangeText,
      placeholder,
      prefixComponent,
      required,
      size = 'default',
      suffixComponent,
      width,
      testID,
      value,
      defaultValue,
      ...props
    }: AllInputProps,
    ref,
  ) => {
    const internalId = useId();
    const id = baseID || internalId;

    const { inputRef, handleChange: handleMaskChange, formatValue } = useInputMask(mask);
    const { formattedValue, formattedDefaultValue } = useFormattedValues(value as string, defaultValue as string, mask);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      // Apply mask formatting
      handleMaskChange(event);

      if (onChange) {
        onChange(event);
      }
      if (onChangeText) {
        onChangeText(event.currentTarget.value);
      }
    };

    // This is needed for react-hook-form defaultValues to be masked correctly
    useEffect(() => {
      if (mask && inputRef.current) {
        const value = inputRef.current.value;
        inputRef.current.value = formatValue(value as string);
      }
    }, [mask, formatValue]);

    return (
      <div
        className={cx('fp-input-container', containerClassName)}
        data-has-error={hasError}
        data-size={size}
        data-disabled={disabled}
      >
        {label && (
          <Label hasError={hasError} size={size} htmlFor={id}>
            {label}
          </Label>
        )}
        <div className="relative" style={{ width: width || 'inherit' }}>
          {prefixComponent && (
            <div className="absolute top-0 left-0 h-full flex items-center pl-3">{prefixComponent}</div>
          )}
          <input
            {...props}
            aria-required={required}
            className={cx(
              'fp-input fp-custom-appearance',
              suffixComponent ? 'pr-6!' : size === 'compact' ? 'pr-3' : 'pr-4',
              prefixComponent ? 'pl-9!' : size === 'compact' ? 'pl-3' : 'pl-4',
              className,
            )}
            data-has-error={hasError}
            data-has-focus={hasFocus}
            data-size={size}
            data-testid={testID}
            disabled={disabled}
            id={id}
            onChange={handleChange}
            placeholder={disabled ? undefined : placeholder}
            required={required}
            tabIndex={0}
            value={formattedValue}
            defaultValue={formattedDefaultValue}
            ref={mergeRefs([ref, inputRef])}
          />
          {suffixComponent && (
            <div className="absolute top-0 right-0 h-full flex items-center pr-2">{suffixComponent}</div>
          )}
        </div>
        {hint && <Hint hasError={hasError}>{hint}</Hint>}
      </div>
    );
  },
);

export default BaseInput;
